import React, { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Blinds, DoorClosed, Droplets, ShowerHead, DoorOpen, Ruler, Rows3, CircleDashed, Frame } from "lucide-react";
import Reveal from "@/components/Reveal";

// Tap-to-reveal micro-areas Ascendia targets. Icons + short labels, and on tap
// a concise "what we do / why it matters" note. No project photos required.
const ITEMS = [
  { icon: Blinds, label: "Window tracks", detail: "Grime and grit hide in the runners. We lift it out so windows glide and look finished." },
  { icon: DoorClosed, label: "Cabinet hinges", detail: "Hinges and pivots collect greasy dust. We degrease them so doors close clean and quiet." },
  { icon: Droplets, label: "Faucet bases", detail: "The seam where the tap meets the counter traps limescale. We clear it back to the metal." },
  { icon: ShowerHead, label: "Shower tracks", detail: "Door channels hold soap scum and mildew. We detail the tracks, not just the glass." },
  { icon: DoorOpen, label: "Door tops", detail: "The one surface nobody checks. We wipe the tops so there's no grey line left behind." },
  { icon: Ruler, label: "Baseboards", detail: "Skirting and trim gather scuffs and dust. We wipe them down edge to edge." },
  { icon: Rows3, label: "Drawer runners", detail: "Crumbs and dust sit in the sliders. We clear them so drawers run smoothly." },
  { icon: CircleDashed, label: "Drains", detail: "Sink and shower drains build residue. We clean the visible drain and surround." },
  { icon: Frame, label: "Corners & edges", detail: "Where walls, floors and fixtures meet is where build-up hides. We work every corner." },
];

export const WhatAscendiaSees = () => {
  const [openIdx, setOpenIdx] = useState(null);
  const reduce = useReducedMotion();
  const active = openIdx !== null ? ITEMS[openIdx] : null;

  return (
    <div data-testid="what-ascendia-sees">
      <p className="mb-5 text-center text-xs text-muted-c">Tap any detail to see what we do there.</p>
      <div className="grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-9 gap-3">
        {ITEMS.map((it, i) => {
          const on = openIdx === i;
          return (
            <Reveal key={it.label} delay={i * 0.04}>
              <button
                type="button"
                onClick={() => setOpenIdx((p) => (p === i ? null : i))}
                data-testid={`was-hotspot-${i}`}
                aria-expanded={on}
                aria-pressed={on}
                className={`group ascendia-card flex w-full flex-col items-center justify-center gap-2 p-4 text-center transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--ring)] ${on ? "border-[color:var(--border-strong)] bg-gold/[0.06]" : "hover:border-[color:var(--border-strong)]"}`}
              >
                <span className={`flex h-10 w-10 items-center justify-center rounded-lg border transition-colors duration-150 ${on ? "bg-gold/20 border-[color:var(--border-strong)]" : "bg-gold/10 border-[color:var(--border-subtle)]"}`}>
                  <it.icon className="h-5 w-5 text-gold-soft" strokeWidth={1.5} />
                </span>
                <span className="text-[11px] leading-tight text-secondary">{it.label}</span>
              </button>
            </Reveal>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        {active && (
          <motion.div
            key={openIdx}
            initial={reduce ? false : { opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: -6 }}
            transition={{ duration: reduce ? 0 : 0.2 }}
            className="ascendia-card mt-4 flex items-start gap-3 p-5"
            data-testid="was-detail-panel"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gold/10 border border-[color:var(--border-subtle)]">
              <active.icon className="h-4 w-4 text-gold-soft" strokeWidth={1.6} />
            </span>
            <div>
              <p className="text-sm font-semibold text-white">{active.label}</p>
              <p className="mt-1 text-sm text-secondary">{active.detail}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WhatAscendiaSees;
